/**
 * conversionCalculator.js
 * Handles ceramic tile → linear meter (skirting/border) conversion math.
 *
 * Core concept:
 *   A tile is cut along one dimension (the "cutting width").
 *   The cut produces strips whose LENGTH = the other dimension of the tile.
 *   Each strip = one linear meter piece of the target product.
 *
 * Example 1: 60×60 tile → 10cm skirting (cut width = 0.10m)
 *   strips per tile = floor(0.60 / 0.10) = 6
 *   each strip length = 0.60m
 *   total linear meters per tile = 6 × 0.60 = 3.6m
 *
 * Example 2: 60×60 tile → 20cm border (cut width = 0.20m)
 *   strips per tile = floor(0.60 / 0.20) = 3
 *   each strip length = 0.60m
 *   total linear meters per tile = 3 × 0.60 = 1.8m
 *
 * Example 3: 30×60 tile → 10cm skirting (cut along 0.30m side)
 *   strips per tile = floor(0.30 / 0.10) = 3
 *   each strip length = 0.60m
 *   total linear meters per tile = 3 × 0.60 = 1.8m
 */

export const round = (n, p = 10) =>
  Math.round(n * Math.pow(10, p)) / Math.pow(10, p);

/**
 * Get the two cuttable dimensions from a source product.
 * For m² products with W×L dimensions, returns both sides.
 * For linear_meter products, returns the single length.
 *
 * @param {object} sourceProduct - product doc with sizePerItem, itemWidth*, itemLength*
 *                                 (* stored as attributes or passed separately)
 * @param {number} sourceWidth   - width of source tile in meters (user-provided if not on product)
 * @param {number} sourceLength  - length of source tile in meters
 * @returns {{ sides: [{label, value}], area: number } | null}
 */
export function getSourceDimensions(sourceProduct, sourceWidth, sourceLength) {
  const unit = sourceProduct?.category?.measurementUnit || "pcs";

  if (unit !== "m2") return null;

  const sizePerItem = sourceProduct?.sizePerItem;
  if (!sizePerItem) return null;

  // If width and length provided, use them
  if (sourceWidth > 0 && sourceLength > 0) {
    const sides = [];
    // Deduplicate for square tiles (60×60 — both sides the same)
    if (Math.abs(sourceWidth - sourceLength) < 0.001) {
      sides.push({ label: `${sourceWidth * 100}cm side`, value: sourceWidth });
    } else {
      sides.push({ label: `${sourceWidth * 100}cm side (width)`,  value: sourceWidth  });
      sides.push({ label: `${sourceLength * 100}cm side (length)`, value: sourceLength });
    }
    return { sides, area: round(sourceWidth * sourceLength) };
  }

  // Fallback: derive sides from sizePerItem only (square tile assumption)
  const side = +Math.sqrt(sizePerItem).toFixed(4);
  return {
    sides: [{ label: `${side * 100}cm side`, value: side }],
    area:  sizePerItem,
  };
}

/**
 * Core conversion: tiles → linear meters
 *
 * @param {number} sourceTotalM2   - total m² of source material being converted
 * @param {number} sourceWidth     - width of ONE source tile in meters (e.g. 0.60)
 * @param {number} sourceLength    - length of ONE source tile in meters (e.g. 0.60 or 0.30)
 * @param {number} cuttingWidth    - width of each cut strip in meters (e.g. 0.10 for 10cm skirting)
 * @param {number} cuttingSide     - which side of the tile is being cut across
 *                                   must be either sourceWidth or sourceLength
 *                                   e.g. 0.60 → we're cutting 0.10cm strips from the 0.60m side
 * @returns {{
 *   stripsPerTile:      number,  — how many strips from one tile
 *   stripLength:        number,  — length of each strip in meters = the OTHER dimension
 *   linearMetersPerTile:number,  — stripsPerTile × stripLength
 *   totalLinearMeters:  number,  — total for all converted tiles
 *   totalTiles:         number,  — how many whole tiles in sourceTotalM2
 *   wastageM2:          number,  — m² lost to cutting (remainder strips too narrow)
 *   formula:            string,  — human-readable formula shown in UI
 * } | null}
 */
export function calcTileToLinearMeter(
  sourceTotalM2,
  sourceWidth,
  sourceLength,
  cuttingWidth,
  cuttingSide,
) {
  if (!sourceTotalM2 || !sourceWidth || !sourceLength ||
      !cuttingWidth  || !cuttingSide) return null;

  if (cuttingWidth <= 0 || cuttingWidth > cuttingSide) return null;

  const tileArea  = round(sourceWidth * sourceLength);
  if (tileArea <= 0) return null;

  // The "other" dimension = the length of each cut strip
  const isWidthSide = Math.abs(cuttingSide - sourceWidth) < 0.001;
  const stripLength = isWidthSide ? sourceLength : sourceWidth;

  // How many whole strips fit along the cutting side?
  const stripsPerTile = Math.floor(round(cuttingSide / cuttingWidth));
  if (stripsPerTile <= 0) return null;

  const linearMetersPerTile = round(stripsPerTile * stripLength);

  // Total whole tiles in source material
  const totalTiles = round(sourceTotalM2 / tileArea);

  // Total linear meters
  const totalLinearMeters = round(totalTiles * linearMetersPerTile);

  // Wastage: the leftover strip that's too narrow to use
  const usedWidth    = round(stripsPerTile * cuttingWidth);
  const wasteWidth   = round(cuttingSide - usedWidth);
  const wastageM2    = round((wasteWidth / cuttingSide) * sourceTotalM2);

  const formula = [
    `${sourceTotalM2} m² ÷ ${tileArea} m²/tile = ${totalTiles} tiles`,
    `${cuttingSide * 100}cm ÷ ${cuttingWidth * 100}cm = ${stripsPerTile} strips/tile`,
    `each strip = ${stripLength * 100}cm long = ${stripLength}m`,
    `${totalTiles} × ${stripsPerTile} × ${stripLength}m = ${totalLinearMeters}m`,
    wasteWidth > 0
      ? `wastage: ${wasteWidth * 100}cm × ${stripLength}m × ${totalTiles} tiles = ${wastageM2.toFixed(4)} m²`
      : `no wastage — tiles cut evenly`,
  ].join(' → ');

  return {
    stripsPerTile,
    stripLength,
    linearMetersPerTile,
    totalLinearMeters,
    totalTiles,
    wastageM2,
    formula,
  };
}

/**
 * Simplified entry point used by the conversion form.
 * Wraps calcTileToLinearMeter for the common case.
 *
 * @param {number} sourceTotalM2  - total m² being converted
 * @param {number} sourceWidth    - source tile width in meters
 * @param {number} sourceLength   - source tile length in meters
 * @param {number} cuttingWidth   - width of each cut strip in meters
 * @param {number} cuttingSide    - which tile side is being cut (sourceWidth or sourceLength)
 * @returns {number|null}         - total linear meters produced
 */
export function calcLinearMeterConversion(
  sourceTotalM2,
  sourceWidth,
  sourceLength,
  cuttingWidth,
  cuttingSide,
) {
  const result = calcTileToLinearMeter(
    sourceTotalM2, sourceWidth, sourceLength, cuttingWidth, cuttingSide
  );
  return result ? result.totalLinearMeters : null;
}